import { useState } from 'react';
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Table,
  Skeleton,
  Tabs,
  NumberInput,
  Badge,
  SimpleGrid,
  Card,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconSparkles,
  IconShoppingCartPlus,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useSuggestedOrder, useOrderPatterns, useAddToCart } from '@/hooks/usePortalApi';
import type { SuggestedItem, OrderPatternItem } from '@/types';

export default function SmartReorder() {
  const { data: suggestions, isLoading: loadingSuggestions } = useSuggestedOrder();
  const { data: patterns, isLoading: loadingPatterns } = useOrderPatterns();
  const addToCart = useAddToCart();

  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [addingToCart, setAddingToCart] = useState(false);

  const getQuantity = (productId: number, defaultQty: number) => {
    return quantities[productId] ?? defaultQty;
  };

  const setQuantity = (productId: number, qty: number) => {
    setQuantities((prev) => ({ ...prev, [productId]: Math.max(0, qty) }));
  };

  // Calculate KPIs
  const estimatedTotal = suggestions?.suggested_items?.reduce((sum: number, item: SuggestedItem) => {
    const qty = getQuantity(item.product_id, item.suggested_qty);
    return sum + item.unit_price * qty;
  }, 0) || 0;

  const avgWeeklyValue = patterns?.avg_weekly_value || 0;
  const weeklyTrend = patterns?.trend || 0;

  const handleAddAllToCart = async () => {
    if (!suggestions?.suggested_items || suggestions.suggested_items.length === 0) {
      return;
    }

    setAddingToCart(true);
    try {
      let successCount = 0;
      for (const item of suggestions.suggested_items) {
        const qty = getQuantity(item.product_id, item.suggested_qty);
        if (qty > 0) {
          await addToCart.mutateAsync({
            product_id: item.product_id,
            quantity: qty,
          });
          successCount++;
        }
      }

      if (successCount > 0) {
        notifications.show({
          title: 'Success',
          message: `Added ${successCount} item${successCount > 1 ? 's' : ''} to cart`,
          color: 'green',
        });
      }
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add items to cart',
        color: 'red',
      });
    } finally {
      setAddingToCart(false);
    }
  };

  const handleAddItem = async (item: SuggestedItem) => {
    const qty = getQuantity(item.product_id, item.suggested_qty);
    if (qty <= 0) {
      notifications.show({
        title: 'Invalid Quantity',
        message: 'Please enter a quantity greater than 0',
        color: 'orange',
      });
      return;
    }

    try {
      await addToCart.mutateAsync({
        product_id: item.product_id,
        quantity: qty,
      });
      notifications.show({
        title: 'Success',
        message: `Added ${item.product_name} to cart`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add item to cart',
        color: 'red',
      });
    }
  };

  const isLoading = loadingSuggestions || loadingPatterns;

  const renderPatternTable = (items: OrderPatternItem[], color: string) => {
    if (!items || items.length === 0) {
      return (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          No items in this category
        </Text>
      );
    }

    return (
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            <Table.Th>Frequency</Table.Th>
            <Table.Th>Avg Quantity</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((item) => (
            <Table.Tr key={item.product_id}>
              <Table.Td>
                <div>
                  <Text fw={500}>{item.product_name}</Text>
                  <Text size="xs" c="dimmed">
                    SKU: {item.sku}
                  </Text>
                </div>
              </Table.Td>
              <Table.Td>
                <Badge color={color} variant="light">
                  {item.frequency}%
                </Badge>
              </Table.Td>
              <Table.Td>{item.avg_qty.toFixed(1)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Smart Reorder"
        description="AI-powered ordering suggestions based on your purchase history"
      />

      {/* KPI Cards */}
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="xs">
            <Text size="sm" c="dimmed" fw={500}>
              Estimated Total
            </Text>
            {isLoading ? (
              <Skeleton height={32} width={120} />
            ) : (
              <Text size="xl" fw={700}>
                ${estimatedTotal.toFixed(2)}
              </Text>
            )}
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="xs">
            <Text size="sm" c="dimmed" fw={500}>
              Avg Weekly Value
            </Text>
            {isLoading ? (
              <Skeleton height={32} width={120} />
            ) : (
              <Text size="xl" fw={700}>
                ${avgWeeklyValue.toFixed(2)}
              </Text>
            )}
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" c="dimmed" fw={500}>
                Weekly Trend
              </Text>
              {!isLoading &&
                (weeklyTrend > 0 ? (
                  <IconTrendingUp size={16} color="green" />
                ) : weeklyTrend < 0 ? (
                  <IconTrendingDown size={16} color="red" />
                ) : (
                  <IconMinus size={16} color="gray" />
                ))}
            </Group>
            {isLoading ? (
              <Skeleton height={32} width={120} />
            ) : (
              <Group gap="xs">
                <Text size="xl" fw={700}>
                  {weeklyTrend > 0 ? '+' : ''}
                  {weeklyTrend.toFixed(1)}%
                </Text>
                <Badge
                  color={weeklyTrend > 0 ? 'green' : weeklyTrend < 0 ? 'red' : 'gray'}
                  variant="light"
                >
                  {weeklyTrend > 0 ? 'Increasing' : weeklyTrend < 0 ? 'Decreasing' : 'Stable'}
                </Badge>
              </Group>
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* AI Suggestions */}
      <Paper shadow="sm" p="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                AI Suggested Items
              </Text>
              <Text size="sm" c="dimmed">
                Based on your ordering patterns and inventory levels
              </Text>
            </div>
            {suggestions?.suggested_items && suggestions.suggested_items.length > 0 && (
              <Button
                leftSection={<IconShoppingCartPlus size={18} />}
                onClick={handleAddAllToCart}
                loading={addingToCart}
                disabled={isLoading}
              >
                Add All to Cart
              </Button>
            )}
          </Group>

          {isLoading ? (
            <Stack gap="xs">
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </Stack>
          ) : !suggestions?.suggested_items || suggestions.suggested_items.length === 0 ? (
            <EmptyState
              title="No Suggestions Available"
              description="We need more order history to generate suggestions. Start placing orders to see AI-powered recommendations."
              icon={<IconSparkles size={48} />}
            />
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Suggested Qty</Table.Th>
                  <Table.Th>Unit Price</Table.Th>
                  <Table.Th>Total</Table.Th>
                  <Table.Th>Reason</Table.Th>
                  <Table.Th>Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {suggestions.suggested_items.map((item: SuggestedItem) => {
                  const qty = getQuantity(item.product_id, item.suggested_qty);
                  const unitPrice = item.unit_price;
                  const total = unitPrice * qty;

                  return (
                    <Table.Tr key={item.product_id}>
                      <Table.Td>
                        <div>
                          <Text fw={500}>{item.product_name}</Text>
                          <Text size="xs" c="dimmed">
                            SKU: {item.sku}
                          </Text>
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          value={qty}
                          onChange={(value) =>
                            setQuantity(item.product_id, Number(value) || 0)
                          }
                          min={0}
                          max={9999}
                          style={{ width: 100 }}
                        />
                      </Table.Td>
                      <Table.Td>${unitPrice.toFixed(2)}</Table.Td>
                      <Table.Td>
                        <Text fw={600}>${total.toFixed(2)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {item.reason}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconShoppingCartPlus size={16} />}
                          onClick={() => handleAddItem(item)}
                          loading={addToCart.isPending}
                          disabled={qty <= 0}
                        >
                          Add
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      {/* Order Patterns Tabs */}
      <Paper shadow="sm" p="md" withBorder>
        <Stack gap="md">
          <div>
            <Text size="lg" fw={600}>
              Order Patterns
            </Text>
            <Text size="sm" c="dimmed">
              Products categorized by order frequency
            </Text>
          </div>

          {isLoading ? (
            <Stack gap="xs">
              <Skeleton height={40} />
              <Skeleton height={200} />
            </Stack>
          ) : !patterns?.always_items?.length &&
            !patterns?.sometimes_items?.length &&
            !patterns?.occasional_items?.length ? (
            <EmptyState
              title="No Pattern Data"
              description="Not enough order history to identify patterns. Keep ordering to see insights."
              icon={<IconSparkles size={48} />}
            />
          ) : (
            <Tabs defaultValue="always">
              <Tabs.List>
                <Tabs.Tab value="always">
                  Always Ordered
                  {patterns?.always_items && patterns.always_items.length > 0 && (
                    <Badge size="sm" ml="xs" circle>
                      {patterns.always_items.length}
                    </Badge>
                  )}
                </Tabs.Tab>
                <Tabs.Tab value="sometimes">
                  Sometimes Ordered
                  {patterns?.sometimes_items && patterns.sometimes_items.length > 0 && (
                    <Badge size="sm" ml="xs" circle>
                      {patterns.sometimes_items.length}
                    </Badge>
                  )}
                </Tabs.Tab>
                <Tabs.Tab value="occasional">
                  Occasional
                  {patterns?.occasional_items && patterns.occasional_items.length > 0 && (
                    <Badge size="sm" ml="xs" circle>
                      {patterns.occasional_items.length}
                    </Badge>
                  )}
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="always" pt="md">
                {renderPatternTable(patterns?.always_items || [], 'green')}
              </Tabs.Panel>

              <Tabs.Panel value="sometimes" pt="md">
                {renderPatternTable(patterns?.sometimes_items || [], 'blue')}
              </Tabs.Panel>

              <Tabs.Panel value="occasional" pt="md">
                {renderPatternTable(patterns?.occasional_items || [], 'gray')}
              </Tabs.Panel>
            </Tabs>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
